import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../lib/LangContext";
import { localizedCategory } from "../../lib/i18n";
import { CATEGORIES } from "../../lib/products";

const WHATSAPP_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || "971500000000").replace(
  /[^\d]/g,
  ""
);

function WhatsAppIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden
    >
      <path
        fill={color}
        d="M16 3C8.82 3 3 8.82 3 16c0 2.29.6 4.44 1.66 6.31L3 29l6.86-1.8A12.94 12.94 0 0 0 16 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.6a10.6 10.6 0 0 1-5.4-1.47l-.38-.22-4.07 1.07 1.09-3.97-.25-.4A10.6 10.6 0 1 1 26.6 16 10.6 10.6 0 0 1 16 26.6zm5.87-7.94c-.32-.16-1.9-.94-2.2-1.05-.29-.11-.5-.16-.72.16-.21.32-.83 1.05-1.02 1.26-.19.22-.37.24-.69.08-.32-.16-1.36-.5-2.6-1.6-.96-.86-1.62-1.94-1.81-2.26-.19-.32-.02-.5.14-.65.14-.14.32-.37.48-.55.16-.19.21-.32.32-.54.11-.22.05-.4-.03-.56-.08-.16-.72-1.74-1-2.38-.26-.62-.53-.53-.72-.54h-.62c-.22 0-.56.08-.86.4-.29.32-1.13 1.1-1.13 2.68 0 1.58 1.16 3.1 1.32 3.32.16.22 2.28 3.48 5.53 4.88.77.33 1.38.53 1.85.68.78.25 1.49.21 2.05.13.62-.09 1.9-.78 2.17-1.54.27-.75.27-1.4.19-1.54-.08-.14-.29-.22-.61-.38z"
      />
    </svg>
  );
}

export default function WhatsAppQuote() {
  const { dict, isRTL } = useLang();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const message = useMemo(() => {
    const parts = [dict.wa_greeting, ""];
    if (category) parts.push(`${dict.wa_line_family}: ${category}`);
    if (qty) parts.push(`${dict.wa_line_qty}: ${qty}`);
    if (note) parts.push(`${dict.wa_line_note}: ${note}`);
    parts.push("");
    parts.push(`— ${dict.wa_sent_from}`);
    return parts.join("\n");
  }, [category, qty, note, dict]);

  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

  const posClass = isRTL ? "left-5 md:left-8" : "right-5 md:right-8";

  return (
    <>
      {/* Floating action button */}
      <motion.button
        data-testid="whatsapp-fab"
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, y: 30, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        aria-label="WhatsApp Quick Quote"
        className={`fixed ${posClass} bottom-6 md:bottom-8 z-40 group flex items-center gap-3 pl-4 pr-5 py-3 bg-[#1A1918] text-[#F7F5F0] hover:bg-[#B56B45] transition-colors duration-500 shadow-[0_18px_40px_-16px_rgba(26,25,24,0.55)]`}
      >
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F7F5F0] text-[#1A1918] group-hover:bg-[#1A1918] group-hover:text-[#F7F5F0] transition-colors duration-500"
          aria-hidden
        >
          <WhatsAppIcon size={16} />
        </span>
        <span className="label text-[10px]">{dict.wa_fab}</span>
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-[#1A1918]/40 backdrop-blur-sm"
              data-testid="whatsapp-backdrop"
            />

            {/* Card */}
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className={`fixed z-50 ${posClass} bottom-24 md:bottom-28 w-[calc(100vw-2.5rem)] md:w-[440px] bg-[#F7F5F0] border hairline shadow-[0_40px_80px_-30px_rgba(26,25,24,0.6)]`}
              data-testid="whatsapp-panel"
              role="dialog"
              aria-modal="true"
            >
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between">
                  <span className="label text-[10px] text-[#5C5A55]">{dict.wa_kicker}</span>
                  <button
                    onClick={() => setOpen(false)}
                    className="label text-[10px] link-under text-[#1A1918]"
                    data-testid="whatsapp-close"
                    aria-label={dict.wa_close}
                  >
                    {dict.wa_close}
                  </button>
                </div>

                <h3 className="serif font-light text-3xl md:text-4xl leading-[1] tracking-[-0.02em] mt-4 text-[#1A1918]">
                  {dict.wa_title} <span className="italic">{dict.wa_title_em}</span>
                  <span className="text-[#B56B45]">.</span>
                </h3>
                <p className="mt-3 text-[14px] leading-[1.65] text-[#5C5A55] max-w-[42ch]">
                  {dict.wa_body}
                </p>

                <div className="mt-6">
                  <label className="label text-[10px] text-[#5C5A55]">
                    {dict.wa_field_category}
                  </label>
                  <select
                    data-testid="whatsapp-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="field"
                  >
                    <option value="">{dict.wa_field_category_ph}</option>
                    {CATEGORIES.map((c) => {
                      const loc = localizedCategory(dict, c.id);
                      return (
                        <option key={c.id} value={loc.title}>
                          {loc.title}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="mt-4">
                  <label className="label text-[10px] text-[#5C5A55]">{dict.wa_field_qty}</label>
                  <input
                    data-testid="whatsapp-qty"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder={dict.wa_field_qty_ph}
                    className="field"
                  />
                </div>

                <div className="mt-4">
                  <label className="label text-[10px] text-[#5C5A55]">{dict.wa_field_note}</label>
                  <textarea
                    data-testid="whatsapp-note"
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={dict.wa_field_note_ph}
                    className="field"
                  />
                </div>

                {/* Preview */}
                <div className="mt-6 border-t hairline pt-4">
                  <div className="label text-[9px] text-[#5C5A55] mb-2">
                    {dict.wa_preview}
                  </div>
                  <pre
                    className="serif text-[13px] leading-[1.55] text-[#1A1918] whitespace-pre-wrap max-h-[110px] overflow-y-auto"
                    style={{ fontFamily: isRTL ? "Amiri, serif" : "Cormorant Garamond, serif" }}
                    data-testid="whatsapp-preview"
                  >
                    {message}
                  </pre>
                </div>

                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="whatsapp-send"
                  onClick={() => setTimeout(() => setOpen(false), 300)}
                  className="mt-6 inline-flex items-center justify-center gap-3 w-full px-6 py-4 bg-[#1A1918] text-[#F7F5F0] label text-[10px] hover:bg-[#B56B45] transition-colors duration-500"
                >
                  <WhatsAppIcon size={14} />
                  {dict.wa_send}
                  <span aria-hidden>{isRTL ? "←" : "→"}</span>
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
